import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import { useToast } from '../Toast';
import BulkEmailPanel from './communications/BulkEmailPanel';
import NewsletterListPanel from './communications/newsletter/NewsletterListPanel';

export default function CommunicationsEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const [activeTab, setActiveTab] = useState('newsletter');
  const [drafts, setDrafts] = useState({
    admin: { 
      subject: '', 
      greeting: '', 
      body: '', 
      bullets: ['', '', ''], 
      ctaLabel: '', 
      footer: '' 
    },
    client: { 
      subject: '', 
      greeting: '', 
      body: '', 
      bullets: ['', '', ''], 
      ctaLabel: '', 
      footer: '' 
    },
    employee: { 
      subject: '', 
      greeting: '', 
      body: '', 
      bullets: ['', '', ''], 
      ctaLabel: '', 
      footer: '' 
    }
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Update drafts whenever config.emailTemplates changes
    if (config.emailTemplates) {
      const mergeTemplate = (templateData) => {
        // Preserve bullets array from database, or use defaults if not present
        return {
          subject: templateData?.subject || '',
          greeting: templateData?.greeting || '',
          body: templateData?.body || '',
          bullets: Array.isArray(templateData?.bullets) && templateData.bullets.length > 0
            ? templateData.bullets
            : ['', '', ''],
          ctaLabel: templateData?.ctaLabel || '',
          footer: templateData?.footer || ''
        };
      };
      
      const newDrafts = {
        admin: mergeTemplate(config.emailTemplates.admin),
        client: mergeTemplate(config.emailTemplates.client),
        employee: mergeTemplate(config.emailTemplates.employee)
      };
      
      setDrafts(newDrafts);
      setIsLoaded(true);
    } else if (config.enrollmentContent && !isLoaded) {
      // Migrate old enrollment content to new structure (only once)
      setDrafts(prev => ({
        ...prev,
        employee: {
          subject: config.enrollmentContent.subject || '',
          greeting: config.enrollmentContent.greeting || '',
          body: config.enrollmentContent.body || '',
          bullets: Array.isArray(config.enrollmentContent.bullets) && config.enrollmentContent.bullets.length > 0
            ? config.enrollmentContent.bullets
            : ['', '', ''],
          ctaLabel: config.enrollmentContent.ctaLabel || '',
          footer: config.enrollmentContent.footer || ''
        }
      }));
      setIsLoaded(true);
    }
  }, [config.emailTemplates, config.enrollmentContent, isLoaded]);

  const updateDraft = (type, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const updateBullet = (type, index, value) => {
    setDrafts(prev => {
      const newBullets = [...prev[type].bullets];
      newBullets[index] = value;
      return {
        ...prev,
        [type]: {
          ...prev[type],
          bullets: newBullets
        }
      };
    });
    setHasChanges(true);
  };

  const addBullet = (type) => {
    setDrafts(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        bullets: [...prev[type].bullets, '']
      }
    }));
    setHasChanges(true);
  };

  const removeBullet = (type, index) => {
    setDrafts(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        bullets: prev[type].bullets.filter((_, i) => i !== index)
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updateConfig({ emailTemplates: drafts });
      showToast('Email templates updated successfully', 'success');
      setHasChanges(false);
    } catch (err) {
      showToast('Failed to save changes', 'error');
    }
  };

  const renderEditor = (type, config) => {
    const currentDraft = drafts[type];

    return (
      <div>
        <div style={{ marginBottom: 24, padding: 16, background: C.blueLight, borderRadius: 8, borderLeft: `4px solid ${C.accent}` }}>
          <p style={{ margin: 0, fontSize: 13, color: C.textMid, lineHeight: 1.6 }}>
            <strong>ℹ️ {config.description}</strong><br />
            {config.note}
          </p>
        </div>

        {/* Email Subject */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ ...S.label, marginBottom: 8 }}>Email Subject</label>
          <input
            type="text"
            value={currentDraft.subject}
            onChange={(e) => updateDraft(type, 'subject', e.target.value)}
            placeholder={config.subjectPlaceholder}
            style={{ ...S.input, fontSize: 14 }}
          />
        </div>

        {/* Greeting */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ ...S.label, marginBottom: 8 }}>Greeting</label>
          <input
            type="text"
            value={currentDraft.greeting}
            onChange={(e) => updateDraft(type, 'greeting', e.target.value)}
            placeholder="Hi {{first_name}},"
            style={{ ...S.input, fontSize: 14 }}
          />
        </div>

        {/* Email Body */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ ...S.label, marginBottom: 8 }}>Email Body</label>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: C.textLight }}>
            Main message that appears in the email
          </p>
          <textarea
            value={currentDraft.body}
            onChange={(e) => updateDraft(type, 'body', e.target.value)}
            placeholder={config.bodyPlaceholder}
            style={{
              ...S.input,
              minHeight: 120,
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Bullet Points */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ ...S.label, marginBottom: 8 }}>**What you need to do:** (Optional Steps/Benefits)</label>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: C.textLight }}>
            Add 3-6 key points that will appear as numbered steps or benefits
          </p>
          
          {currentDraft.bullets.map((bullet, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={bullet}
                  onChange={(e) => updateBullet(type, idx, e.target.value)}
                  placeholder={`${idx + 1}. Step or benefit ${idx + 1}`}
                  style={{ ...S.input, fontSize: 13 }}
                />
              </div>
              {currentDraft.bullets.length > 1 && (
                <button
                  onClick={() => removeBullet(type, idx)}
                  style={{
                    padding: '8px 12px',
                    background: C.red,
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  <Icon name="trash-outline" style={{ fontSize: 14 }} />
                </button>
              )}
            </div>
          ))}

          {currentDraft.bullets.length < 6 && (
            <button
              onClick={() => addBullet(type)}
              style={{
                padding: '8px 16px',
                background: C.bg,
                color: C.accent,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <Icon name="add-outline" style={{ fontSize: 16 }} />
              Add Step
            </button>
          )}
        </div>

        {/* CTA Button Label */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ ...S.label, marginBottom: 8 }}>CTA Button Label</label>
          <input
            type="text"
            value={currentDraft.ctaLabel}
            onChange={(e) => updateDraft(type, 'ctaLabel', e.target.value)}
            placeholder="Log In to Review & Enroll"
            style={{ ...S.input, fontSize: 14 }}
          />
        </div>

        {/* Footer */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ ...S.label, marginBottom: 8 }}>Footer</label>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: C.textLight }}>
            Additional message at the end (urgency, deadline, etc.)
          </p>
          <textarea
            value={currentDraft.footer}
            onChange={(e) => updateDraft(type, 'footer', e.target.value)}
            placeholder="This enrollment window closes on {{deadline}}. Don't miss out on potential tax savings!"
            style={{
              ...S.input,
              minHeight: 60,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Available Variables */}
        <div style={{ padding: 16, background: C.bg, borderRadius: 8, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMid, marginBottom: 8 }}>
            Available Variables:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {config.variables.map(v => (
              <code key={v} style={{ 
                background: 'white', 
                padding: '4px 8px', 
                borderRadius: 4, 
                fontSize: 11, 
                color: C.accent,
                border: `1px solid ${C.border}`
              }}>
                {v}
              </code>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    {
      id: 'bulk',
      label: 'Bulk Email',
      icon: 'send-outline',
      isBulk: true,
    },
    {
      id: 'newsletter',
      label: 'Newsletter',
      icon: 'newspaper-outline',
      isNewsletter: true,
    },
    { 
      id: 'employee', 
      label: 'Employee Comms',
      icon: 'people-outline',
      description: 'Sent to employees for benefits enrollment',
      note: 'These emails invite employees to enroll in the Section 125 benefits program.',
      subjectPlaceholder: 'Your {{company_name}} Employee Benefits Enrollment is Ready',
      bodyPlaceholder: "We're excited to share that {{company_name}} is offering a new employee benefits program through HealthCues Section 125 tax savings plans.\n\nThis is an opportunity to reduce your taxable income and save money on health insurance premiums - all while keeping the exact same coverage you have today.",
      variables: ['{{company_name}}', '{{first_name}}', '{{last_name}}', '{{deadline}}', '{{login_link}}', '{{temp_password}}']
    },
    { 
      id: 'client', 
      label: 'Client Comms',
      icon: 'business-outline',
      description: 'Sent to employer contacts and decision makers',
      note: 'These emails invite employer contacts to access the client portal.',
      subjectPlaceholder: '{{inviter_name}} invited you to {{company_name}} on HealthCues',
      bodyPlaceholder: "{{inviter_name}} has invited you to join {{company_name}} on HealthCues.\n\nAccess your employer dashboard to manage benefits, track tax savings, and support your employees through the enrollment process.",
      variables: ['{{company_name}}', '{{inviter_name}}', '{{first_name}}', '{{last_name}}', '{{invite_link}}', '{{temp_password}}', '{{expiry_hours}}']
    },
    { 
      id: 'admin', 
      label: 'Admin Comms',
      icon: 'settings-outline',
      description: 'Sent to internal admin users and account managers',
      note: 'These emails invite internal team members to access the admin platform.',
      subjectPlaceholder: '{{inviter_name}} invited you to HealthCues',
      bodyPlaceholder: "{{inviter_name}} has invited you to join the HealthCues platform.\n\nAccess the admin dashboard to manage clients, analyze benefits programs, and support employer success.",
      variables: ['{{inviter_name}}', '{{company_name}}', '{{first_name}}', '{{last_name}}', '{{invite_link}}', '{{temp_password}}', '{{expiry_hours}}']
    }
  ];

  return (
    <div style={{ maxWidth: (activeTab === 'bulk' || activeTab === 'newsletter') ? 1400 : 900, margin: '0 auto', padding: '20px 0' }}>
      <div style={S.card}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ ...S.heading, margin: '0 0 8px' }}>Communications</h2>
          <p style={{ margin: 0, fontSize: 14, color: C.textMid }}>
            Manage email content for different user types. Customize the headline and key benefits shown in invitation emails.
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 32, borderBottom: `2px solid ${C.border}` }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 20px',
                background: activeTab === tab.id ? C.accent : 'transparent',
                color: activeTab === tab.id ? 'white' : C.textMid,
                border: 'none',
                borderBottom: activeTab === tab.id ? `2px solid ${C.accent}` : '2px solid transparent',
                borderRadius: '6px 6px 0 0',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s ease',
                marginBottom: -2
              }}
            >
              <Icon name={tab.icon} style={{ fontSize: 18 }} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Active Tab Content */}
        {activeTab === 'bulk'
          ? <BulkEmailPanel />
          : activeTab === 'newsletter'
            ? <NewsletterListPanel />
            : renderEditor(activeTab, tabs.find(t => t.id === activeTab))}

        {/* Save Button — template-editor tabs only */}
        {activeTab !== 'bulk' && activeTab !== 'newsletter' && hasChanges && (
          <div style={{ marginTop: 32, paddingTop: 24, borderTop: `1px solid ${C.border}`, display: 'flex', gap: 12 }}>
            <button
              onClick={handleSave}
              style={{
                padding: '12px 24px',
                background: C.accent,
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(14, 116, 144, 0.2)'
              }}
            >
              Save All Changes
            </button>
            <button
              onClick={() => {
                setDrafts({
                  admin: config.emailTemplates?.admin || { headline: '', bullets: ['', '', ''] },
                  client: config.emailTemplates?.client || { headline: '', bullets: ['', '', ''] },
                  employee: config.emailTemplates?.employee || config.enrollmentContent || { headline: '', bullets: ['', '', ''] }
                });
                setHasChanges(false);
              }}
              style={{
                padding: '12px 24px',
                background: C.bg,
                color: C.textMid,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
